import { getRefreshToken, request } from '@/api/client'

// POST /app/auth/send-sms
export interface SendSmsRequest { phone: string }
export interface OtpChallengeResponse {
  challenge_id: string
  expire_seconds: number
  rate_seconds: number
}
export interface AuthCapabilities {
  providers: Array<'phone_otp' | 'email_otp' | 'google' | 'apple'>
  phone_country_codes: string[]
  google_enabled: boolean
  apple_enabled: boolean
}

export const getAuthCapabilities = () =>
  request<AuthCapabilities>('GET', '/app/auth/capabilities', {
    skipAuth: true,
  })
export interface SendSmsResponse { expire_seconds: number; rate_seconds: number }
export const sendSms = (data: SendSmsRequest) =>
  request<SendSmsResponse>('POST', '/app/auth/send-sms', {
    body: data,
    skipAuth: true,
  })

// POST /app/auth/send-email
export interface SendEmailRequest { email: string }
export interface SendEmailResponse { expire_seconds: number; rate_seconds: number }
export const sendEmail = (data: SendEmailRequest) =>
  request<SendEmailResponse>('POST', '/app/auth/send-email', {
    body: data,
    skipAuth: true,
  })

export const sendPhoneLoginCode = (phone: string) =>
  request<OtpChallengeResponse>('POST', '/app/auth/phone/send-code', {
    body: { phone, purpose: 'login' },
    skipAuth: true,
  })

export const sendEmailLoginCode = (email: string) =>
  request<OtpChallengeResponse>('POST', '/app/auth/email/send-code', {
    body: { email, purpose: 'login' },
    skipAuth: true,
  })

// POST /app/auth/login
export interface LoginDeviceInfo {
  device_id: string
  platform: string
  os: string
  language: string
  timezone: string
  screen_width: number
  screen_height: number
  user_agent: string
  brand: string | null
  device_model: string | null
}
export interface LoginRequest {
  phone?: string
  email?: string
  otp: string
  device_id?: string
  device_info?: LoginDeviceInfo
  agreed_terms: boolean
}
export interface UserInfo {
  user_id: string
  nickname: string
  avatar: string | null
  phone?: string | null
  email?: string | null
  points: number
  is_vip: boolean
  vip_expire_time: string | null
  vip_entitlement?: VipEntitlement | null
}

export type VipNoticeType =
  | 'none'
  | 'fixed_term_expiring'
  | 'auto_renew_scheduled'
  | 'auto_renew_disabled_expiring'
  | 'billing_retry'
  | 'grace'
  | 'expired'

export interface VipEntitlement {
  subscription_id: string
  state_version: string
  status: 'active' | 'grace' | 'expired'
  purchase_mode: 'auto_renewable' | 'fixed_term'
  expires_at: string
  expires_on: string
  remaining_days: number
  is_auto_renew: boolean
  renewal_at: string | null
  notice_type: VipNoticeType
  timezone: 'Asia/Shanghai'
}
export interface LoginResponse {
  user: UserInfo
  access_token: string
  refresh_token: string
  expires_in: number
  refresh_expires_in: number
  session_absolute_expires_at: string
}
export const login = (data: LoginRequest) =>
  request<LoginResponse>('POST', '/app/auth/login', {
    body: data,
    skipAuth: true,
  })

export interface OtpLoginRequest {
  challenge_id: string
  otp: string
  device_id?: string
  device_info?: LoginDeviceInfo
  agreed_terms: boolean
}

export const loginWithPhone = (data: OtpLoginRequest & { phone: string }) =>
  request<LoginResponse>('POST', '/app/auth/phone/login', {
    body: data,
    skipAuth: true,
  })

export const loginWithEmail = (data: OtpLoginRequest & { email: string }) =>
  request<LoginResponse>('POST', '/app/auth/email/login', {
    body: data,
    skipAuth: true,
  })

export interface SocialLoginBaseRequest {
  device_id?: string
  device_info?: LoginDeviceInfo
  agreed_terms: boolean
}

export const loginWithGoogle = (data: SocialLoginBaseRequest & { id_token: string }) =>
  request<LoginResponse>('POST', '/app/auth/google/login', {
    body: data,
    skipAuth: true,
  })

export const loginWithApple = (data: SocialLoginBaseRequest & {
  id_token: string
  authorization_code: string
  nonce: string
}) =>
  request<LoginResponse>('POST', '/app/auth/apple/login', {
    body: data,
    skipAuth: true,
  })

// POST /app/auth/logout
export const logout = () =>
  request<null>('POST', '/app/auth/logout', {
    body: { refresh_token: getRefreshToken() },
  })
